import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STORE_KEY = "aetryx_uploads";
const UploadContext = createContext(null);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const loadSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]").map((s) => ({ ...s, status: "needs-file", speed: 0, eta: null }));
  } catch {
    return [];
  }
};

export function UploadProvider({ children }) {
  const [items, setItems] = useState(loadSaved);
  const runtime = useRef({});

  const patch = useCallback((id, p) => setItems((list) => list.map((i) => (i.asset_id === id ? { ...i, ...p } : i))), []);

  useEffect(() => {
    const persist = items.filter((i) => !["ready", "error"].includes(i.status)).map(({ asset_id, filename, size, kind, lastModified, progress, target, meta }) => ({ asset_id, filename, size, kind, lastModified, progress, target, meta }));
    localStorage.setItem(STORE_KEY, JSON.stringify(persist));
  }, [items]);

  const putChunk = async (id, index, blob, signal) => {
    for (let attempt = 0; ; attempt++) {
      try {
        await api.put(`/uploads/${id}/chunks/${index}`, blob, { headers: { "Content-Type": "application/octet-stream" }, signal, timeout: 120000 });
        return;
      } catch (e) {
        if (signal.aborted || attempt >= 3 || e?.response?.status === 400 || e?.response?.status === 403) throw e;
        await sleep(1000 * 2 ** attempt);
      }
    }
  };

  const run = useCallback(async (id) => {
    const rt = runtime.current[id];
    const { file } = rt;
    rt.controller = new AbortController();
    try {
      const { data: asset } = await api.get(`/uploads/${id}`);
      if (asset.status === "ready") {
        patch(id, { status: "ready", progress: 1, url: asset.url });
        return rt.resolve?.(asset);
      }
      const received = new Set(asset.received);
      let done = received.size * asset.chunk_size;
      patch(id, { status: "uploading", progress: Math.min(1, done / asset.size) });
      let windowStart = Date.now();
      let windowBytes = 0;
      for (let i = 0; i < asset.total_chunks; i++) {
        if (received.has(i)) continue;
        if (rt.paused) return patch(id, { status: "paused", speed: 0, eta: null });
        const blob = file.slice(i * asset.chunk_size, Math.min(file.size, (i + 1) * asset.chunk_size));
        await putChunk(id, i, blob, rt.controller.signal);
        done += blob.size;
        windowBytes += blob.size;
        const secs = (Date.now() - windowStart) / 1000;
        const speed = windowBytes / Math.max(secs, 0.1);
        if (secs > 8) { windowStart = Date.now(); windowBytes = 0; }
        patch(id, { progress: Math.min(1, done / asset.size), speed, eta: (asset.size - done) / Math.max(speed, 1) });
      }
      patch(id, { status: "processing", speed: 0, eta: null });
      const { data: ready } = await api.post(`/uploads/${id}/complete`);
      patch(id, { status: "ready", progress: 1, url: ready.url, link_status: ready.link_status });
      rt.resolve?.(ready);
      const detail = { asset_id: id, url: ready.url, target: ready.target, meta: rt.meta, handled: false };
      window.dispatchEvent(new CustomEvent("aetryx:upload-ready", { detail }));
      if (!detail.handled && ready.link_status === "linked") toast.success(`${ready.filename} attached to its title`);
    } catch (e) {
      if (rt.paused) return patch(id, { status: "paused", speed: 0, eta: null });
      patch(id, { status: "error", error: formatApiError(e), speed: 0, eta: null });
    }
  }, [patch]);

  const startUpload = useCallback(async (file, kind, target = null, meta = null, onStarted) => {
    const { data } = await api.post("/uploads/init", { filename: file.name, size: file.size, kind, target });
    onStarted?.(data.asset_id);
    const item = { asset_id: data.asset_id, filename: file.name, size: file.size, kind, lastModified: file.lastModified, status: "queued", progress: 0, speed: 0, eta: null, target, meta };
    setItems((list) => [item, ...list]);
    const promise = new Promise((resolve) => { runtime.current[data.asset_id] = { file, resolve, meta }; });
    run(data.asset_id);
    return promise;
  }, [run]);

  const bindDraft = useCallback(async (draftKey, titleId) => {
    try {
      await api.post("/uploads/bind", { draft_key: draftKey, title_id: titleId });
      setItems((list) => list.map((i) => (i.target?.draft_key === draftKey ? { ...i, target: { ...i.target, title_id: titleId } } : i)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const pause = (id) => {
    const rt = runtime.current[id];
    if (!rt) return;
    rt.paused = true;
    rt.controller?.abort();
  };

  const resume = (id, file) => {
    const item = items.find((i) => i.asset_id === id);
    const rt = runtime.current[id] || {};
    if (file) {
      if (file.name !== item.filename || file.size !== item.size) return toast.error("That's a different file — select the original file to resume.");
      rt.file = file;
    }
    if (!rt.file) return patch(id, { status: "needs-file" });
    rt.meta = rt.meta || item.meta;
    rt.paused = false;
    runtime.current[id] = rt;
    patch(id, { status: "uploading", error: null });
    run(id);
  };

  const dismiss = (id) => {
    const item = items.find((i) => i.asset_id === id);
    pause(id);
    delete runtime.current[id];
    setItems((list) => list.filter((i) => i.asset_id !== id));
    if (item && item.status !== "ready") api.delete(`/uploads/${id}`).catch(() => {});
  };

  return <UploadContext.Provider value={{ items, startUpload, bindDraft, pause, resume, dismiss }}>{children}</UploadContext.Provider>;
}

export const useUploads = () => useContext(UploadContext);
